import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, Tag, User, Share2, ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";

const BlogPost = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { slug } = useParams();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/api/blog/${slug}`);
        setPost(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch manuscript. It may have been moved or deleted.");
        console.error("Error fetching post:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, apiUrl]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-[#09090B]">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-[#09090B] px-4">
        <div className="text-center p-10 rounded-3xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 shadow-xl max-w-md w-full">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8" />
          </div>
          <p className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-8">{error || "Manuscript not found"}</p>
          <button 
            onClick={() => navigate("/blog")} 
            className="w-full inline-flex justify-center items-center px-6 py-3 bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 font-medium rounded-full hover:bg-zinc-800 dark:hover:bg-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Return to Articles
          </button>
        </div>
      </div>
    );
  }

  // Handle image URLs properly like in the editor
  const heroImage = post.image?.startsWith("http") ? post.image : `${apiUrl}/${post.image}`;

  return (
    <motion.article 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      transition={{ duration: 0.5 }}
      className="min-h-screen py-24 bg-[#FAFAFA] dark:bg-[#09090B]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Minimal Back Navigation */}
        <button 
          onClick={() => navigate("/blog")}
          className="group flex items-center text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors mb-12"
        >
          <div className="p-2 rounded-full bg-zinc-100 dark:bg-white/5 mr-3 group-hover:bg-zinc-200 dark:group-hover:bg-white/10 transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </div>
          Back to all articles
        </button>

        {/* Header Area */}
        <header className="mb-14">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags?.map((tag) => (
                <span 
                  key={tag} 
                  className="inline-flex items-center px-3 py-1 rounded-md text-[11px] font-semibold tracking-wider uppercase bg-zinc-100 text-zinc-600 dark:bg-white/5 dark:text-zinc-400 border border-zinc-200 dark:border-white/5"
                >
                  <Tag className="w-3 h-3 mr-1.5" />{tag}
                </span>
              ))}
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-semibold text-zinc-900 dark:text-white mb-8 leading-[1.1] tracking-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-medium text-zinc-600 dark:text-zinc-400">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2 text-zinc-400 dark:text-zinc-500" />
                {post.author?.name || "Gebre Wagnew"}
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-zinc-400 dark:text-zinc-500" />
                {new Intl.DateTimeFormat('en-US', { dateStyle: 'long' }).format(new Date(post.createdAt || Date.now()))}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2 text-emerald-600 dark:text-emerald-400" />
                {post.readTime || "5 min read"}
              </div>
            </div>
          </motion.div>
        </header>

        {/* Featured Image */}
        {post.image && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.5, delay: 0.2 }} 
            className="mb-16 relative rounded-3xl overflow-hidden border border-zinc-200 dark:border-white/10 shadow-2xl bg-zinc-100 dark:bg-zinc-900 aspect-[21/9]"
          >
            <img 
              src={heroImage} 
              alt={post.title} 
              className="w-full h-full object-cover" 
            />
          </motion.div>
        )}

        {/* Content Area with Premium Typography */}
        <div className="max-w-3xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            transition={{ duration: 0.6, delay: 0.3 }}
            className="
              prose prose-lg dark:prose-invert max-w-none 
              prose-headings:font-display prose-headings:font-semibold prose-headings:tracking-tight
              prose-a:text-emerald-600 dark:prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline
              prose-pre:bg-zinc-900 prose-pre:border prose-pre:border-zinc-800 dark:prose-pre:bg-zinc-950 dark:prose-pre:border-white/10
              prose-img:rounded-2xl prose-img:border prose-img:border-zinc-200 dark:prose-img:border-white/10
              prose-blockquote:border-l-emerald-500 prose-blockquote:bg-zinc-50 dark:prose-blockquote:bg-zinc-900/50 prose-blockquote:py-1 prose-blockquote:rounded-r-lg
              text-zinc-700 dark:text-zinc-300 leading-relaxed
            "
          >
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </motion.div>

          {/* Minimal Share Section Using Native SVGs */}
          <div className="mt-20 pt-10 border-t border-zinc-200 dark:border-white/10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center text-zinc-900 dark:text-white font-medium">
              <Share2 className="w-5 h-5 mr-3 text-zinc-400" /> 
              Share this manuscript
            </div>
            <div className="flex items-center gap-3">
              
              <button 
                onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`, "_blank")} 
                className="inline-flex items-center justify-center p-3 rounded-full bg-zinc-100 dark:bg-white/5 text-zinc-600 dark:text-zinc-400 hover:bg-[#1DA1F2] hover:text-white dark:hover:bg-[#1DA1F2] transition-colors"
                aria-label="Share on X"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </button>

              <button 
                onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`, "_blank")} 
                className="inline-flex items-center justify-center p-3 rounded-full bg-zinc-100 dark:bg-white/5 text-zinc-600 dark:text-zinc-400 hover:bg-[#0A66C2] hover:text-white dark:hover:bg-[#0A66C2] transition-colors"
                aria-label="Share on LinkedIn"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd"/>
                </svg>
              </button>

            </div>
          </div>
        </div>

      </div>
    </motion.article>
  );
};

export default BlogPost;